/**
 * FB-1443 — PDP media gallery: announce which thumbnail is the current one.
 *
 * Finding 81569 (Critical, WCAG 4.1.2, `aria-name-missing-incorrect`). The
 * auditor's expected name for a thumbnail ends `", current item,"` — the
 * screen-reader rendering of `aria-current`. Nothing in the theme exposed
 * that state, so a screen reader user hearing "View product image 3" had no
 * way to know image 3 was already the one on display.
 *
 * Why this is JS and not markup: the active thumbnail is identified only by
 * the *absence* of the `opacity-30` class, which vendor `assets/theme.js`
 * toggles from the `xSplide` store's `mounted move` handler (~L1793). Liquid
 * renders every thumbnail dimmed and the vendor undims one after Splide
 * mounts, so a static `aria-current` in the template would be wrong from the
 * first slide change onward — worse than none, because it would assert a
 * stale state rather than omit an unknown one.
 *
 * Mirroring the class here covers all three strips the PDP renders — the main
 * rail, the quick-view rail and the zoom-modal rail — plus any rebuilt by
 * `variant-select.js`, which swaps `x-thumbnail` on and off nodes when a
 * variant changes its media set. No vendor file is touched and no call site
 * changes.
 *
 * `aria-current="true"` is the correct attribute rather than `aria-selected`
 * or `aria-pressed`: the rail is a set of links-in-spirit to one gallery, not
 * a tablist (no `role="tab"` anywhere) and not a toggle. `true` is used over a
 * token like `"page"` because the thumbnail selects media within the current
 * page, not a location.
 *
 * Deliberately NOT done: naming the thumbnails from their media's alt text.
 * That is the other half of 81569 and is a content task — every media item on
 * the audited product ships `alt=""`, so it would change no announcement
 * today. `snippets/media-thumbnail-label.liquid` is where it would go.
 */
(() => {
  'use strict';

  // Set by an earlier execution of this file. The gallery snippet can render
  // more than once per page (PDP plus quick view), so guard against a second
  // copy double-observing every strip.
  if (window.__fbA11yMediaGallery) return;
  window.__fbA11yMediaGallery = true;

  /** Applied by Liquid to every thumbnail; removed by theme.js from the active one. */
  const DIMMED_CLASS = 'opacity-30';

  const STRIP_SELECTOR = '[id^="x-product-thumbnail-"], [id^="x-zoom-thumbnail-"]';

  const observed = new WeakSet();

  const syncThumbnail = (thumbnail) => {
    const button = thumbnail.querySelector('button');
    if (!button) return;

    if (thumbnail.classList.contains(DIMMED_CLASS)) {
      button.removeAttribute('aria-current');
    } else {
      button.setAttribute('aria-current', 'true');
    }
  };

  const syncStrip = (strip) => {
    strip.querySelectorAll('.x-thumbnail').forEach(syncThumbnail);
  };

  const watch = (strip) => {
    if (observed.has(strip)) return;
    observed.add(strip);

    syncStrip(strip);

    // Re-syncing the whole strip on any mutation is cheaper than tracking
    // which node changed: a rail holds one thumbnail per media item, and a
    // slide change mutates two of them (the outgoing and the incoming).
    //
    // This cannot loop. The observer watches `class`; the sync writes
    // `aria-current`, and only on a descendant button.
    new MutationObserver(() => syncStrip(strip)).observe(strip, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  const scan = () => document.querySelectorAll(STRIP_SELECTOR).forEach(watch);

  scan();

  // Quick-view galleries are ajax-rendered on demand, so their rails may not
  // exist at load. Catching up on the first pointer-down inside one costs a
  // single delegated listener, where a document-wide childList observer would
  // run on every DOM write the page makes.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.x-thumbnail')) scan();
    },
    true
  );
})();
